import "@/App.css";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CompanyProvider } from "./context/CompanyContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Setup from "./pages/Setup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Parties from "./pages/Parties";
import PartyProfile from "./pages/PartyProfile";
import Items from "./pages/Items";
import Invoices from "./pages/Invoices";
import InvoiceForm from "./pages/InvoiceForm";
import InvoiceDetail from "./pages/InvoiceDetail";
import Payments from "./pages/Payments";
import Purchases from "./pages/Purchases";
import Expenses from "./pages/Expenses";
import Reports from "./pages/Reports";
import CreditNotes from "./pages/CreditNotes";
import Delivery from "./pages/Delivery";
import HR from "./pages/HR";
import CashBank from "./pages/CashBank";
import WorkOrders from "./pages/WorkOrders";
import RateComparison from "./pages/RateComparison";
import Approvals from "./pages/Approvals";
import Recurring from "./pages/Recurring";
import Assets from "./pages/Assets";
import Documents from "./pages/Documents";
import { DocDetail, DocForm, DocList } from "./pages/Docs";
import HelpCenter from "./pages/HelpCenter";
import Settings from "./pages/Settings";
import { Loader2 } from "lucide-react";

function Protected() {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-[#0B0F17]" data-testid="auth-loading">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return (
    <CompanyProvider>
      <Layout />
    </CompanyProvider>
  );
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/setup" element={<Setup />} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route element={<Protected />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/parties" element={<Parties />} />
              <Route path="/parties/:id" element={<PartyProfile />} />
              <Route path="/items" element={<Items />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/invoices/new" element={<InvoiceForm />} />
              <Route path="/invoices/:id" element={<InvoiceDetail />} />
              <Route path="/payments" element={<Payments />} />
              <Route path="/quotations" element={<DocList kind="quotation" />} />
              <Route path="/quotations/new" element={<DocForm kind="quotation" />} />
              <Route path="/quotations/:id" element={<DocDetail kind="quotation" />} />
              <Route path="/sales-orders" element={<DocList kind="sales_order" />} />
              <Route path="/sales-orders/new" element={<DocForm kind="sales_order" />} />
              <Route path="/sales-orders/:id" element={<DocDetail kind="sales_order" />} />
              <Route path="/proformas" element={<DocList kind="proforma" />} />
              <Route path="/proformas/new" element={<DocForm kind="proforma" />} />
              <Route path="/proformas/:id" element={<DocDetail kind="proforma" />} />
              <Route path="/purchases" element={<Purchases />} />
              <Route path="/purchases/orders/new" element={<DocForm kind="purchase_order" />} />
              <Route path="/purchases/invoices/new" element={<DocForm kind="purchase_invoice" />} />
              <Route path="/expenses" element={<Expenses />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/credit-notes" element={<CreditNotes />} />
              <Route path="/delivery" element={<Delivery />} />
              <Route path="/hr" element={<HR />} />
              <Route path="/cash-bank" element={<CashBank />} />
              <Route path="/work-orders" element={<WorkOrders />} />
              <Route path="/rate-comparison" element={<RateComparison />} />
              <Route path="/approvals" element={<Approvals />} />
              <Route path="/recurring" element={<Recurring />} />
              <Route path="/assets" element={<Assets />} />
              <Route path="/documents" element={<Documents />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
